import { useChartColors } from '@components/Common/Charts/useChartColors';
import EmptyState from '@components/Common/EmptyState';
import { SectionCard } from '@components/Common/EntityWorkspace';
import { MetricColumn } from '@components/Common/MetricTable';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { aiUsageSummaryQuery } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import { LineChart } from '@mui/x-charts/LineChart';
import React, { useEffect, useState } from 'react';
import {
  AiFeatureRow,
  AiUsageSummary,
  count,
  dayLabel,
  featureLabel,
  monthRange,
  notAttributedCents,
  serviceLabel,
  usd,
  usdPrecise,
} from './costData';
import { CostSection } from './CostParts';

/*
 * OpenAI spend by feature, from the ai_usage ledger the backend and the external jobs service
 * write on every call. OpenAI bills the organisation; the ledger says which feature spent it. The
 * two never match exactly (the website's own calls are not in the ledger, and billing lags), so the
 * gap is shown as its own "Not attributed" row rather than left for the reader to subtract.
 */

interface Row extends AiFeatureRow {
  /** The billed-minus-ledger row, which has no calls or tokens. */
  gap?: boolean;
}

const NOT_ATTRIBUTED = 'Not attributed';

const COLUMNS: MetricColumn<Row>[] = [
  { key: 'feature', header: 'Feature', variant: 'name', render: (r) => (r.gap ? NOT_ATTRIBUTED : featureLabel(r.feature)) },
  { key: 'service', header: 'Service', align: 'left', render: (r) => (r.gap ? '-' : serviceLabel(r.service)) },
  { key: 'model', header: 'Model', align: 'left', render: (r) => r.model || '-' },
  { key: 'calls', header: 'Calls', align: 'right', render: (r) => (r.gap ? '-' : count(r.calls)), sortValue: (r) => r.calls },
  { key: 'in', header: 'Tokens in', align: 'right', render: (r) => (r.gap ? '-' : count(r.tokensIn)), sortValue: (r) => r.tokensIn },
  { key: 'out', header: 'Tokens out', align: 'right', render: (r) => (r.gap ? '-' : count(r.tokensOut)), sortValue: (r) => r.tokensOut },
  {
    key: 'cost',
    header: 'Cost (USD)',
    align: 'right',
    render: (r) => <span className="ac-amount">{usdPrecise(r.costUsd)}</span>,
    sortValue: (r) => r.costUsd,
  },
];

/** The ledger's rows, largest first, with the billed gap appended when OpenAI billed more. */
export function featureRows(summary: AiUsageSummary | null, gapCents: number | null): Row[] {
  const rows: Row[] = [...(summary?.byFeature ?? [])].sort((a, b) => b.costUsd - a.costUsd);
  if (gapCents != null && gapCents > 0) {
    rows.push({ feature: NOT_ATTRIBUTED, service: '', model: null, calls: 0, tokensIn: 0, tokensOut: 0, costUsd: gapCents / 100, gap: true });
  }
  return rows;
}

const DailyChart: React.FC<{ days: AiUsageSummary['byDay'] }> = ({ days }) => {
  const [colour] = useChartColors(1);
  if (!days.length) return <EmptyState compact title="No recorded AI calls this month" />;
  return (
    <div className="ac-chart">
      <LineChart
        xAxis={[{ data: days.map((d) => dayLabel(d.date)), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
        yAxis={[{ valueFormatter: (v: number | null) => (v == null ? '' : `$${v.toFixed(2)}`) }]}
        series={[
          {
            data: days.map((d) => d.costUsd),
            label: 'Cost (USD)',
            color: colour,
            curve: 'linear',
            valueFormatter: (v: number | null) => usdPrecise(v ?? 0),
          },
        ]}
        height={240}
        margin={{ top: 16, right: 16, bottom: 32, left: 16 }}
      />
    </div>
  );
};

const AiUsageSection: React.FC<{ month: string; billedCents: number | null }> = ({ month, billedCents }) => {
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    let live = true;
    const { from, to } = monthRange(month);
    setState('loading');
    fetchGraphQLApiData(aiUsageSummaryQuery(from, to))
      .then((r) => {
        if (!live) return;
        const s = r?.data?.aiUsageSummary ?? null;
        setSummary(s);
        setState(s ? 'ready' : 'failed');
      })
      .catch(() => live && setState('failed'));
    return () => {
      live = false;
    };
  }, [month]);

  if (state === 'loading') {
    return (
      <SectionCard title="By feature" icon={<WidgetsOutlinedIcon />}>
        <SkeletonLoader rows={5} columns={5} />
      </SectionCard>
    );
  }
  if (state === 'failed' || !summary) {
    return (
      <SectionCard title="By feature" icon={<WidgetsOutlinedIcon />}>
        <EmptyState
          compact
          title="Feature breakdown unavailable"
          body="The AI usage ledger did not answer. OpenAI's own totals above are unaffected."
        />
      </SectionCard>
    );
  }

  const gap = notAttributedCents(billedCents, summary.totalUsd);
  const rows = featureRows(summary, gap);
  const ledgerCents = Math.round(summary.totalUsd * 100);

  return (
    <>
      <CostSection<Row>
        title="By feature"
        icon={<WidgetsOutlinedIcon />}
        meta={`${summary.byFeature.length} feature${summary.byFeature.length === 1 ? '' : 's'}`}
        columns={COLUMNS}
        rows={rows}
        getRowKey={(r, i) => `${r.feature}-${r.service}-${r.model}-${i}`}
        emptyLabel="No recorded AI calls this month"
        footnote={
          <>
            Recorded by the usage ledger: {usd(ledgerCents)}.{' '}
            {gap == null
              ? 'Connect OpenAI billing to compare this with what OpenAI billed.'
              : gap > 0
                ? `OpenAI billed ${usd(billedCents)}; the ${usd(gap)} difference is shown as Not attributed (calls made outside the ledger, such as the website's own).`
                : gap < 0
                  ? `The ledger is ${usd(-gap)} ahead of OpenAI's billed ${usd(billedCents)}; OpenAI's figures can lag by a day.`
                  : `This matches what OpenAI billed.`}{' '}
            Ledger days are IST; OpenAI bills in UTC.
            {summary.unpriced > 0 && (
              <span className="ac-footnote-line">
                Unpriced calls: {count(summary.unpriced)} call{summary.unpriced === 1 ? '' : 's'} used a model the price
                table does not know, so they are counted at $0 here.
              </span>
            )}
          </>
        }
      />
      <SectionCard title="Daily AI cost" icon={<ShowChartOutlinedIcon />} meta="From the usage ledger">
        <DailyChart days={summary.byDay ?? []} />
      </SectionCard>
    </>
  );
};

export default AiUsageSection;
